"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { NAV_LINKS, SITE, whatsappUrl } from "@/lib/site";
import { IconClose, IconMenu, IconWhatsApp } from "./Icons";
import { LogoMark } from "./LogoMark";

type ServiceLink = { href: string; label: string };

function isActive(pathname: string, href: string) {
  return href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
}

export function SiteHeader({ services }: { services: ServiceLink[] }) {
  const pathname = usePathname() || "/";
  const [open, setOpen] = useState(false);
  // Dropdown desktop: disembunyikan setelah link diklik, muncul lagi saat kursor keluar-masuk.
  const [subClosed, setSubClosed] = useState(false);
  const subParentRef = useRef<HTMLAnchorElement>(null);

  const closeSubAfterClick = () => {
    setSubClosed(true);
    // Lepas fokus agar :focus-within tidak membuat dropdown tetap terbuka.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Tutup menu saat berpindah halaman.
  useEffect(() => setOpen(false), [pathname]);

  // Tutup dengan tombol Escape dan kembalikan fokus ke tombol menu.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="site-header">
      <div className="container header-inner">
        <Link href="/" className="brand">
          <LogoMark size={44} className="brand-mark" />
          <span className="brand-text">
            <span className="brand-name">{SITE.name}</span>
            <span className="brand-tag">{SITE.tagline}</span>
          </span>
        </Link>

        <button
          ref={toggleRef}
          type="button"
          className="nav-toggle"
          aria-expanded={open}
          aria-controls="menu-utama"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <IconClose /> : <IconMenu />}
          <span>{open ? "Tutup" : "Menu"}</span>
        </button>

        <nav id="menu-utama" aria-label="Navigasi utama" className={open ? "main-nav is-open" : "main-nav"}>
          <ul className="nav-list">
            {NAV_LINKS.map((l) =>
              l.href === "/layanan" ? (
                <li
                  key={l.href}
                  className={subClosed ? "has-sub sub-closed" : "has-sub"}
                  onMouseLeave={() => setSubClosed(false)}
                  onFocus={() => setSubClosed(false)}
                  onKeyDown={(e: ReactKeyboardEvent<HTMLLIElement>) => {
                    if (e.key === "Escape") {
                      subParentRef.current?.focus();
                      setSubClosed(true);
                    } else if (e.key === "Tab" || e.key === "ArrowDown") {
                      setSubClosed(false);
                    }
                  }}
                >
                  <Link
                    ref={subParentRef}
                    href={l.href}
                    onClick={closeSubAfterClick}
                    aria-current={isActive(pathname, l.href) ? "page" : undefined}
                  >
                    {l.label}
                  </Link>
                  <ul className="sub-nav" aria-label="Daftar layanan">
                    {services.map((s) => (
                      <li key={s.href}>
                        <Link
                          href={s.href}
                          onClick={closeSubAfterClick}
                          aria-current={pathname === s.href ? "page" : undefined}
                        >
                          {s.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={l.href}>
                  <Link href={l.href} aria-current={isActive(pathname, l.href) ? "page" : undefined}>
                    {l.label}
                  </Link>
                </li>
              ),
            )}
          </ul>
          <a
            className="btn btn-primary nav-cta"
            href={whatsappUrl("Halo Ranutama Teknik Indonesia, saya ingin konsultasi tentang pemasangan tenda membrane.")}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconWhatsApp size={18} />
            Konsultasi Gratis
          </a>
        </nav>
      </div>
    </header>
  );
}
